import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
} from 'chart.js';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import type { ChartComponentProps } from '@app/pages/Analytics/OrganizationClientsGrowth/OrganizationClientsGrowth.types';
import { divideAmount } from '@app/utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const ChartComponent: React.FC<ChartComponentProps> = ({
  organizationClientsGrowth,
}) => {
  const labels = useMemo(
    () => organizationClientsGrowth?.Labels ?? [],
    [organizationClientsGrowth?.Labels]
  );
  const values = useMemo(
    () => organizationClientsGrowth?.Values ?? [],
    [organizationClientsGrowth?.Values]
  );

  const yScale = useMemo(() => {
    if (values.length === 0) {
      return {
        position: 'right' as const,
        min: 0,
        max: 1,
        ticks: { precision: 0 },
      };
    }

    const allEqual = values.every((v) => v === values[0]);

    if (allEqual) {
      const [base] = values;
      const padding = base === 0 ? 1 : Math.abs(base * 0.1);

      return {
        position: 'right' as const,
        min: base - padding,
        max: base + padding,
        ticks: { precision: 0 },
      };
    }

    return {
      position: 'right' as const,
      grace: '10%',
      ticks: { precision: 0 },
    };
  }, [values]);

  const data = useMemo(() => {
    if (values.length === 0) {
      return { labels: [], datasets: [] };
    }

    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: '#47B2FF',
          backgroundColor: 'rgba(117, 197, 255, 0.2)',
          tension: values.length === 1 ? 0 : 0.4,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointHitRadius: 10,
          pointBackgroundColor: '#47B2FF',
        },
      ],
    };
  }, [labels, values]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.5,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            label: (context) =>
              `${divideAmount(Number(context.raw ?? 0))} клиентов`,
          },
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      scales: {
        y: yScale,
        x: {
          ticks: {
            maxRotation: 0,
          },
        },
      },
    }),
    [yScale]
  );

  return (
    <Line data={data} options={options} className="analytic-chart-component" />
  );
};
